import * as Sentry from '@sentry/react';
import { isAxiosError } from 'axios';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import type { User } from '~/types/User';

import { logout, submitOpcLogoutFormRequest } from '~/api/auth';
import { getCurrentUser } from '~/api/currentUser';
import AuthenticationStore from '~/auth/authenticationStore';
import { ssoDebugLog } from '~/auth/ssoDebugLog';
import { useAuthenticationStore } from '~/auth/useAuthenticationStore';
import { identifyAnalyticsUser } from '~/common/analytics/analyticsClient';
import { QueryParamType } from '~/enums/QueryParamType';
import { useStaleWhileRevalidate } from '~/hooks/useStaleWhileRevalidate';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { useClientSlug } from '~/routing/clientSlug';
import { getClientFromUrl, getSubdomainCheckAction } from '~/routing/utils';
import { MetricsService } from '~/services/MetricsService/MetricsService';
import {
  redirectToOAuth,
  redirectToProviderAuthPage,
  redirectToZendesk,
} from '~/services/redirects';
import { logger } from '~/utils/logger';
import { getPageSearchQueryByKey } from '~/utils/searchQuery';
import { captureMessage } from '~/utils/sentry';

const USER_STORAGE_KEY = 'currentUser';

type Props = {
  currentUser: User;
  onSubdomainResolved: (subdomain: string) => void;
};

/**
 * Side-effect-only child that owns the auth-session orchestration App.tsx used
 * to carry: the SSO bounce checkers (Zendesk, Rolodex, Aegis), the subdomain
 * mismatch → redirect/logout flow, the `MetricsService.appMounted` once-per
 * session report, and the "user loaded" Redux/Sentry/AuthStore dispatch.
 *
 * Mounts under `<I18nProvider>` (above the splash gate) so its effects run
 * regardless of whether App has resolved enough state to leave splash.
 * Returns `null` — every interesting output is a side effect or a callback.
 */
function AuthSessionBootstrap({ currentUser, onSubdomainResolved }: Props) {
  const dispatch = useDispatch();
  const { isLoggedIn: isUserLoggedIn } = useAuthenticationStore();

  // Owns the `getCurrentUser` fetch that used to live in App.tsx. We dispatch
  // the result into Redux so App and the rest of the tree only consume
  // `currentUser` from the store.
  // Skip fetching while on the permission-check page: the page owns its own
  // bootstrap flow, and firing /my-profile here creates an infinite redirect
  // loop (423 → redirect to /folders-permission-check → mount → 423 → …).
  const shouldFetchCurrentUser =
    isUserLoggedIn &&
    Object.keys(currentUser).length === 0 &&
    !window.location.pathname.includes('folders-permission-check');
  const { data: user } = useStaleWhileRevalidate(
    USER_STORAGE_KEY,
    () => getCurrentUser(),
    {
      skip: !shouldFetchCurrentUser,
      onError: (error: unknown) => {
        if (isAxiosError(error) && error.response?.status === 423) {
          window.location.href = '/folders-permission-check';
        }
      },
    },
  );

  // Cache-warm: nothing reads the result here, but downstream features rely on
  // the RTK cache being populated. Same pattern as the user fetch above.
  const clientId = (user as User | undefined)?.client;
  api.endpoints.getClient.useQuery(clientId, { skip: !clientId });

  const { origin, pathname } = window.location;

  // Re-derived on slug change (skipping the mount run, which would just
  // repeat the `useRef` initializer below): this sits above `AppDataRouter`'s
  // keyed `RouterProvider`, so it won't remount when `enterClientSlug` swaps
  // clients in place, and would otherwise stay pinned to the tab's original
  // client.
  const clientSlug = useClientSlug();
  const initialClientRef = useRef(getClientFromUrl());
  const hasMountedClientSlugRef = useRef(false);
  useEffect(() => {
    if (!hasMountedClientSlugRef.current) {
      hasMountedClientSlugRef.current = true;
      return;
    }
    initialClientRef.current = getClientFromUrl();
  }, [clientSlug]);
  const oAuthRedirectInProgressRef = useRef(false);
  // Mirrors today's `subdomainRef.current` gate: only let the user-loaded
  // effect resolve subdomain if nothing else has resolved it yet.
  const hasResolvedSubdomainRef = useRef(false);
  // Ensure `MetricsService.appMounted` fires at most once per session even if
  // the user object identity churns from stale-while-revalidate refreshes.
  const hasReportedAppMountedRef = useRef(false);
  // Set synchronously by `checkSubDomain` to stop the effect below from
  // overwriting the target client it just recorded with the account's home one.
  const subdomainMismatchLogoutRef = useRef(false);

  const resolveSubdomain = useCallback(
    (value: string) => {
      hasResolvedSubdomainRef.current = true;
      onSubdomainResolved(value);
    },
    [onSubdomainResolved],
  );

  const zendeskQuerySsoFlag = getPageSearchQueryByKey(QueryParamType.Sso);
  const zendeskQuerySsoAction = getPageSearchQueryByKey(QueryParamType.Action);
  // return_to: Zendesk SSO redirect target, only used when sso=zendesk is also present.
  const zendeskQueryRedirectUrl = getPageSearchQueryByKey<string | null>(
    QueryParamType.ReturnTo,
  );
  // return_url: rolodex /oauth/authorize login redirect. When a user hits rolodex's
  // authorize endpoint without a session, rolodex 302s here with return_url set to the
  // full authorize URL. After login we navigate back so the OAuth flow can complete.
  const oAuthReturnUrl = getPageSearchQueryByKey<string | null>(
    QueryParamType.ReturnUrl,
  );
  const oAuthQueryClientId = getPageSearchQueryByKey<string | null>(
    QueryParamType.ClientId,
  );
  const oAuthQueryResponseType = getPageSearchQueryByKey(
    QueryParamType.ResponseType,
  );
  const oAuthQueryRedirectUri = getPageSearchQueryByKey<string | null>(
    QueryParamType.RedirectUri,
  );
  const oAuthQueryState = getPageSearchQueryByKey<string | null>(
    QueryParamType.State,
  );
  const providerAuthState = getPageSearchQueryByKey<string | null>(
    QueryParamType.State,
    null,
  );
  const providerAuthCode = getPageSearchQueryByKey<string | null>(
    QueryParamType.Code,
    null,
  );

  const checkSubDomain = useCallback(
    async (userSubdomain: string) => {
      subdomainMismatchLogoutRef.current = false;

      if (oAuthRedirectInProgressRef.current) {
        ssoDebugLog('checkSubDomain skipped — OAuth redirect in progress');
        return;
      }

      const clientSubdomain = getClientFromUrl();
      const result = getSubdomainCheckAction({
        pathname,
        userSubdomain,
        clientSubdomain,
        initialClient: initialClientRef.current,
      });

      if (result.action === 'redirect') {
        const details = {
          reason: 'subdomain_mismatch_redirect',
          userSubdomain,
          clientSubdomain,
          redirectTo: result.subdomain,
          initialClient: initialClientRef.current,
          pathname,
        };
        logger.auth('Forced redirect: client subdomain mismatch %o', details);
        captureMessage('Forced redirect: client subdomain mismatch', details);
        window.location.href = `${origin}/${result.subdomain}/`;
        return;
      }

      if (result.action === 'logout') {
        const details = {
          reason: 'subdomain_mismatch',
          userSubdomain,
          clientSubdomain: result.clientSubdomain,
          initialClient: initialClientRef.current,
          pathname,
        };
        logger.auth('Forced logout: client subdomain mismatch %o', details);
        captureMessage('Forced logout: client subdomain mismatch', details);

        const redirectSubdomainContainPublicPath = [
          'activation',
          'change-password',
          'expired-token',
          'external-document-viewer',
          'forgot-password',
        ].includes(result.clientSubdomain);

        // Public-path landings (activation, password reset, etc.) have no
        // meaningful "reload" action — send them straight there.
        if (redirectSubdomainContainPublicPath) {
          try {
            await logout();
          } catch (error) {
            console.error('Logout failed:', error);
          }
          AuthenticationStore().clearAuthentication();
          window.location.href = `${origin}/${result.clientSubdomain}${window.location.search}`;
          return;
        }

        // Arm the "Sign in Required" modal instead of redirecting out from
        // under the user. Record the target client so the modal's Reload
        // doesn't bounce back to the now-defunct session's client.
        subdomainMismatchLogoutRef.current = true;
        AuthenticationStore().markRequiresReauth();
        AuthenticationStore().saveSubDomainInSession(result.clientSubdomain);

        try {
          const response = await logout();
          if (
            response?.workday_logout_url &&
            response?.id_token_hint &&
            response?.oidc_client_id
          ) {
            submitOpcLogoutFormRequest({
              action: response.workday_logout_url,
              id_token_hint: response.id_token_hint,
              oidc_client_id: response.oidc_client_id,
            });

            return;
          }
        } catch (error) {
          console.error('Logout failed:', error);
        }

        AuthenticationStore().clearAuthentication({ requiresReauth: true });
      }
    },
    [origin, pathname],
  );

  const checkRedirectToZendesk = useCallback(
    (loginStatus: boolean): boolean => {
      const zendeskRedirectUrl = AuthenticationStore().getZendeskUrl();
      const zendeskRedirectExp = AuthenticationStore().getZendeskExpire();

      if (!zendeskRedirectUrl && zendeskQuerySsoFlag === 'zendesk') {
        AuthenticationStore().setZendeskUrl(zendeskQueryRedirectUrl ?? '');
      }

      if (
        zendeskQuerySsoFlag === 'zendesk' &&
        zendeskQuerySsoAction === 'logout'
      ) {
        AuthenticationStore().removeZendeskFlag();
        AuthenticationStore().clearAuthentication();
        window.location.href = `${origin}`;
        return true;
      }

      if (zendeskRedirectUrl && loginStatus) {
        if (parseInt(zendeskRedirectExp ?? '0', 10) < new Date().valueOf()) {
          AuthenticationStore().removeZendeskFlag();
        } else {
          redirectToZendesk(zendeskRedirectUrl);
          return true;
        }
      }

      return false;
    },
    [
      origin,
      zendeskQueryRedirectUrl,
      zendeskQuerySsoAction,
      zendeskQuerySsoFlag,
    ],
  );

  const checkRolodexOAuthBounce = useCallback(
    (loginStatus: boolean): boolean => {
      const storedBounceUrl = AuthenticationStore().getRolodexOAuthBounceUrl();
      const storedBounceExp =
        AuthenticationStore().getRolodexOAuthBounceExpire();

      if (!storedBounceUrl && oAuthReturnUrl) {
        AuthenticationStore().setRolodexOAuthBounceUrl(oAuthReturnUrl);
      }

      if (storedBounceUrl && loginStatus) {
        if (parseInt(storedBounceExp ?? '0', 10) < new Date().valueOf()) {
          AuthenticationStore().removeRolodexOAuthBounce();
        } else {
          AuthenticationStore().removeRolodexOAuthBounce();
          window.location.href = storedBounceUrl;
          return true;
        }
      }

      return false;
    },
    [oAuthReturnUrl],
  );

  const checkAegisOAuthProxy = useCallback(
    (loginStatus: boolean): boolean => {
      const [oAuthRedirectUri, oAuthClientId, oAuthState] =
        AuthenticationStore().getAegisOAuthProxyVars();
      const oAuthRedirectExp = AuthenticationStore().getAegisOAuthProxyExpire();

      ssoDebugLog('checkAegisOAuthProxy called', {
        loginStatus,
        oAuthRedirectUri,
        oAuthRedirectExp,
        oAuthQueryResponseType,
        pathname: globalThis.location.pathname,
        search: globalThis.location.search,
      });

      if (!oAuthRedirectUri && oAuthQueryResponseType === 'code') {
        AuthenticationStore().setAegisOAuthProxyVars(
          oAuthQueryRedirectUri ?? '',
          oAuthQueryClientId ?? '',
          oAuthQueryState ?? '',
        );
        ssoDebugLog('Saved OAuth vars to sessionStorage', {
          oAuthQueryRedirectUri,
          oAuthQueryClientId,
          oAuthQueryState,
        });
      }

      if (oAuthRedirectUri && loginStatus) {
        if (parseInt(oAuthRedirectExp ?? '0', 10) < Date.now()) {
          ssoDebugLog('OAuth expired — removing flag', {
            oAuthRedirectExp,
            now: Date.now(),
          });
          AuthenticationStore().removeAegisOAuthProxy();
        } else if (oAuthRedirectInProgressRef.current) {
          ssoDebugLog('Skipped duplicate redirectToOAuth call');
          return true;
        } else {
          oAuthRedirectInProgressRef.current = true;
          ssoDebugLog('Calling redirectToOAuth', {
            oAuthRedirectUri,
            oAuthClientId,
            oAuthState,
          });
          redirectToOAuth(
            oAuthRedirectUri,
            oAuthClientId ?? '',
            oAuthState ?? '',
          );
          return true;
        }
      }

      if (!oAuthRedirectUri && loginStatus) {
        ssoDebugLog(
          'No OAuth vars in sessionStorage — logged in, waiting for re-render',
        );
      }
      if (oAuthRedirectUri && !loginStatus) {
        ssoDebugLog('OAuth vars exist but user not logged in');
      }

      return false;
    },
    [
      oAuthQueryClientId,
      oAuthQueryRedirectUri,
      oAuthQueryResponseType,
      oAuthQueryState,
    ],
  );

  useEffect(() => {
    if (isUserLoggedIn && user && !hasReportedAppMountedRef.current) {
      hasReportedAppMountedRef.current = true;
      MetricsService.appMounted(user);
    }
  }, [isUserLoggedIn, user]);

  useEffect(() => {
    if (user) {
      const { client_config } = user;
      const { sub_domain } = client_config as {
        sub_domain: string;
      };

      void checkSubDomain(sub_domain);

      dispatch(slices.currentUser.actions.setCurrentUser(user));
      // `User.language` is typed as `string | undefined` and `User.locale`
      // isn't modeled at all, but the slices accept the runtime shape — cast
      // through to preserve App.tsx's pre-extraction behavior verbatim.
      const userWithLocaleFields = user as User & {
        language?: string;
        locale?: string;
      };
      dispatch(
        slices.i18n.actions.setLanguage(
          (userWithLocaleFields.language ?? 'en') as Parameters<
            typeof slices.i18n.actions.setLanguage
          >[0],
        ),
      );
      dispatch(
        slices.i18n.actions.setLocale(
          userWithLocaleFields.locale ?? 'system-default',
        ),
      );
      Sentry.setUser({ email: user.email, id: user.id });
      Sentry.setTag('client', sub_domain.toLowerCase());

      // Used to live in the dead `withSplashScreen` HOC; consolidated here so
      // every analytics handoff fires from the same "user resolved" trigger.
      identifyAnalyticsUser(user);

      if (sub_domain && !hasResolvedSubdomainRef.current) {
        resolveSubdomain(sub_domain.toLowerCase());
      }

      // Skip if `checkSubDomain` (above) already recorded the mismatch target —
      // overwriting it here would send Reload back to the client being left.
      if (!subdomainMismatchLogoutRef.current) {
        AuthenticationStore().saveSubDomainInSession(sub_domain);
      }
    }
  }, [checkSubDomain, dispatch, resolveSubdomain, user]);

  useEffect(() => {
    ssoDebugLog('Main useEffect fired', {
      isUserLoggedIn,
      pathname,
      currentUserKeys: Object.keys(currentUser),
      search: globalThis.location.search,
    });

    if (pathname.includes('folders-permission-check')) return;

    if (isUserLoggedIn && pathname.includes('evisync') && providerAuthState) {
      void redirectToProviderAuthPage(
        providerAuthState,
        providerAuthCode ?? '',
      );
      return;
    }

    // These redirects are mutually exclusive (the landing URL will contain only
    // one set of trigger params). Short-circuit so the first match that fires a
    // redirect prevents the others from running in the same tick.
    if (
      checkRolodexOAuthBounce(isUserLoggedIn) ||
      checkRedirectToZendesk(isUserLoggedIn) ||
      checkAegisOAuthProxy(isUserLoggedIn)
    ) {
      return;
    }

    if (!(isUserLoggedIn && Object.keys(currentUser).length === 0)) {
      if (pathname !== '/') {
        resolveSubdomain(getClientFromUrl());
      }
    }
  }, [
    checkAegisOAuthProxy,
    checkRedirectToZendesk,
    checkRolodexOAuthBounce,
    currentUser,
    isUserLoggedIn,
    pathname,
    providerAuthCode,
    providerAuthState,
    resolveSubdomain,
  ]);

  return null;
}

export default AuthSessionBootstrap;
